import {FC, useEffect, useState} from "react";
import {Button, Icon, Modal} from "semantic-ui-react";
import {IInsightAction, InsightActionStatus} from "../api/coachInsightsModels";
import {DataStatus} from "@/features/application/api/applicationModels";
import coachInsightsAPI from "../api/coachInsightsAPI";
import insightActionsStore from "../api/insightActionsStore";
import {ISosBusinessImpactHourlyData} from "../api/coachInsightsModels";
import {formatDuration} from "../api/coachInsightsUtils";
import {LoadingMessage, ErrorMessage} from "@/components/SystemMessages";

interface IProps {
    action: IInsightAction;
    onClose: () => void;
    onResolved: () => void;
}

function windowAvgSosSec(hourlyData: ISosBusinessImpactHourlyData[], startHour: number, endHour: number): number | null {
    const rows = hourlyData.filter(
        (r) => r.hour >= startHour && r.hour <= endHour && r.driveThruTransactions > 0
    );
    if (rows.length === 0) return null;
    const totalWeightedSec = rows.reduce(
        (sum, r) => sum + r.driveThruTotalTimeSec * r.driveThruTransactions,
        0
    );
    const totalTx = rows.reduce((sum, r) => sum + r.driveThruTransactions, 0);
    return totalTx > 0 ? totalWeightedSec / totalTx : null;
}

function verdict(beforeSec: number, afterSec: number): {label: string; className: string} {
    const delta = beforeSec - afterSec;
    if (delta > 10) return {label: "Improved", className: "verdict-improved"};
    if (delta < -10) return {label: "Worse", className: "verdict-worse"};
    return {label: "No Change", className: "verdict-neutral"};
}

const ReviewOutcomeModal: FC<IProps> = ({action, onClose, onResolved}) => {
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.Loading);
    const [beforeSec, setBeforeSec] = useState<number | null>(null);
    const [afterSec, setAfterSec] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const abortController = new AbortController();
        const fetch = async () => {
            setDataStatus(DataStatus.Loading);
            try {
                const result = await coachInsightsAPI.getSosBusinessImpact(
                    action.locationPk,
                    action.businessDate as any,
                    action.timeframeStartHour,
                    action.timeframeEndHour,
                    abortController.signal
                );
                const before = windowAvgSosSec(
                    result.hourlyData,
                    action.timeframeStartHour,
                    action.timeframeEndHour
                );
                const after = windowAvgSosSec(
                    result.wowHourlyData,
                    action.timeframeStartHour,
                    action.timeframeEndHour
                );
                setBeforeSec(before);
                setAfterSec(after);
                setDataStatus(DataStatus.Loaded);
            } catch {
                setDataStatus(DataStatus.Failed);
            }
        };
        fetch();
        return () => abortController.abort();
    }, [action]);

    const handleMark = async (outcome: IInsightAction["reviewOutcome"]) => {
        setSubmitting(true);
        const status =
            outcome === "Improved"
                ? InsightActionStatus.Resolved
                : InsightActionStatus.NotImproved;
        await insightActionsStore.updateActionStatus(action.insightKey, status, outcome);
        setSubmitting(false);
        onResolved();
        onClose();
    };

    const hasData = beforeSec !== null && afterSec !== null;
    const v = hasData ? verdict(beforeSec!, afterSec!) : null;

    return (
        <Modal open size="small" onClose={onClose} className="review-outcome-modal insights-modal">
            <Modal.Header>
                <div className="modal-header-content">
                    <div className="modal-header-left">
                        <Icon name="chart line" />
                        <span className="modal-title">Review Outcome</span>
                    </div>
                    <Icon name="times" link onClick={onClose} />
                </div>
            </Modal.Header>
            <Modal.Content>
                <div className="review-outcome-insight-label">
                    <strong>{action.title}</strong>
                    <span className="review-meta">
                        {action.locationName} &bull; {action.dayPart} &bull; {action.businessDate}
                    </span>
                </div>

                {dataStatus === DataStatus.Loading && <LoadingMessage marginTop={20} />}
                {dataStatus === DataStatus.Failed && (
                    <ErrorMessage inline text="Unable to load comparison data." />
                )}
                {dataStatus === DataStatus.Loaded && (
                    <div className="review-outcome-comparison">
                        <div className="comparison-card before">
                            <div className="comparison-label">When it happened</div>
                            <div className="comparison-value">
                                {beforeSec != null ? formatDuration(beforeSec) : "—"}
                            </div>
                            <div className="comparison-sublabel">avg SOS this window</div>
                        </div>
                        <div className="comparison-arrow">
                            <Icon name="arrow right" />
                        </div>
                        <div className="comparison-card after">
                            <div className="comparison-label">Same window last week</div>
                            <div className="comparison-value">
                                {afterSec != null ? formatDuration(afterSec) : "—"}
                            </div>
                            <div className="comparison-sublabel">avg SOS (WoW)</div>
                        </div>
                        {v && (
                            <div className={`comparison-verdict ${v.className}`}>
                                {v.label}
                            </div>
                        )}
                    </div>
                )}

                {!hasData && dataStatus === DataStatus.Loaded && (
                    <p className="review-outcome-no-data">
                        Not enough comparison data for this window. Mark manually below.
                    </p>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button
                    color="green"
                    loading={submitting}
                    disabled={submitting}
                    onClick={() => handleMark("Improved")}
                >
                    <Icon name="check" />
                    Mark Resolved
                </Button>
                <Button
                    color="orange"
                    loading={submitting}
                    disabled={submitting}
                    onClick={() => handleMark("NoChange")}
                >
                    <Icon name="minus" />
                    No Improvement
                </Button>
                <Button basic onClick={onClose} disabled={submitting}>
                    Close
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default ReviewOutcomeModal;
